import { Injectable } from '@angular/core';
import {
	AbstractCRUDService,
	AlarisLocalStorageService,
	ExtendableRefBookService
} from '@campaign-portal/components-library';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { UsersAndRolesService } from '@helpers/api/users-and-roles.service';
import {
	UsersCreateResponse,
	UsersDeleteRequest,
	UsersDeleteResponse,
	UsersReadRequest,
	UsersReadResponse,
	UsersUpdateResponse
} from '@helpers/types/user-and-roles';
import { User } from '@helpers/types/models';
import { ExportService } from '@helpers/api/export.service';
import { UsersFieldsService } from './users.fields.service';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { STORAGE } from '@helpers/types/consts';
import { exist } from '@campaign-portal/namespace/common/id';
import { ReadResponse } from '@campaign-portal/namespace/common/implementations';

@Injectable({
	providedIn: 'root'
})
export class UsersService extends ExtendableRefBookService<User<exist>> implements AbstractCRUDService {
	override readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	readonly entity = '';
	readonly title = '';
	readonly create = this.update;

	constructor(
		private readonly usersAndRoles: UsersAndRolesService,
		private readonly fields: UsersFieldsService,
		private readonly exportService: ExportService,
		private readonly storage: AlarisLocalStorageService
	) {
		super();
	}

	override load(): Observable<ReadResponse<User<number>[]>> {
		return this.usersAndRoles.loader<ReadResponse<User<number>[]>>('Users.Read', {}, this.loading$).pipe(
			map((resp) => {
				return this.process(resp);
			})
		);
	}

	override process(resp: ReadResponse<User<number>[]>): ReadResponse<User<number>[]> {
		this.list$.next(resp.Data);
		this.map$.next(
			this.list.reduce((result, item) => {
				result.set(item.ID, item);
				return result;
			}, new Map())
		);
		return resp;
	}

	read(params: RPCRequestParams): Observable<UsersReadResponse> {
		const parsedParams: UsersReadRequest = this.prepareParams(params);
		return this.usersAndRoles.loader<UsersReadResponse>('Users.Read', parsedParams, this.loading$);
	}

	update(params: User): Observable<UsersUpdateResponse | UsersCreateResponse> {
		const method = params.ID ? 'Users.Update' : 'Users.Create';
		return this.usersAndRoles.loader<UsersUpdateResponse | UsersCreateResponse>(method, params, this.loading$);
	}

	delete(params: UsersDeleteRequest): Observable<UsersDeleteResponse> {
		return this.usersAndRoles.loader<UsersDeleteResponse>('Users.Delete', params, this.loading$);
	}

	export(total: number): Observable<void> {
		return this.exportService.export(
			this.usersAndRoles.userAndRolesApi.url,
			'Users.Read',
			{
				Paging: {
					Skip: 0,
					Take: total
				}
			},
			this.fields.headers,
			'users'
		);
	}

	private prepareParams(params: RPCRequestParams): UsersReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? parseInt(this.storage.get(STORAGE.PAGE_SIZE) ?? '25', 10),
			active: 'ID',
			direction: 'desc',
			Filters: params.Filters
				? params.Filters.map((filter) => {
						return this.parseFilterField(filter);
				  })
				: []
		};
	}

	private parseFilterField(filter: Filter): { Field: string; Value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'Login':
				return { Field: 'login', Value: value as string };
			case 'Email':
				return { Field: 'email', Value: value as string };
			case 'Details':
				return { Field: 'name', Value: value as string };

			default:
				return { Field: variable, Value: value + '' };
		}
	}
}
